import httpService from "./httpService";
import type { FactorsResponse } from "./productFactorsService";

// Types

export interface StrategyEntry {
  entityType: "PORTFOLIO" | "BASKET";
  productId: number;
  weight: string;
  position: "LONG" | "SHORT";
  driftedWeight: number | null;
}

export interface ManagedStrategyEntry {
  entityType: "MANAGED_STRATEGY";
  productId: number;
  weight: string;
  position: "LONG" | "SHORT";
  driftedWeight: number | null;
}

export type InvestorStrategyRecord = StrategyEntry | ManagedStrategyEntry;

type StrategySyncInput = {
  entityType: "PORTFOLIO" | "BASKET" | "MANAGED_STRATEGY";
  productId: number;
  weight: number;
  position: "LONG";
};

// Entity types the self-directed editors (legacy Strategy page, admin strategy
// editor) can render and therefore express in a save payload.
const SELF_DIRECTED_ENTITY_TYPES: ReadonlyArray<string> = ["PORTFOLIO", "BASKET"];

// Single source of truth for "can a self-directed editor render this?". Every
// filter that splits the account into the editable view and the entries an
// editor cannot show must go through here, so the two halves can never drift
// apart when a new entity type is introduced.
export function isSelfDirectedRecord(
  record: InvestorStrategyRecord
): record is StrategyEntry {
  return SELF_DIRECTED_ENTITY_TYPES.includes(record.entityType);
}

// Entity types the admin strategy editor renders. It shows managed strategies
// as first-class, editable rows, so -- unlike the self-directed editors -- a
// managed entry omitted from its payload is a deliberate removal and must not
// be re-attached. Anything outside this list is still preserved on save.
const ADMIN_EDITABLE_ENTITY_TYPES: ReadonlyArray<string> = [
  "PORTFOLIO",
  "BASKET",
  "MANAGED_STRATEGY",
];

export function isAdminEditableRecord(record: InvestorStrategyRecord): boolean {
  return ADMIN_EDITABLE_ENTITY_TYPES.includes(record.entityType);
}

// Append any currently-held entry the caller cannot express, so a save from an
// editor that never rendered it cannot delete it. Entries already present in the
// payload are left untouched (the caller wins).
export function withPreservedEntries(
  strategies: StrategySyncInput[],
  current: InvestorStrategyRecord[],
  /**
   * "Can the calling editor render this record?" Anything it CANNOT render is
   * preserved. Defaults to the self-directed predicate so every existing caller
   * behaves exactly as before.
   */
  canRender: (record: InvestorStrategyRecord) => boolean = isSelfDirectedRecord
): StrategySyncInput[] {
  const inPayload = new Set(
    strategies.map((entry) => `${entry.entityType}-${entry.productId}`)
  );
  const preserved = current
    .filter((record) => !canRender(record))
    .filter((record) => !inPayload.has(`${record.entityType}-${record.productId}`))
    .map((record) => ({
      entityType: record.entityType,
      productId: record.productId,
      // Preserve the weight verbatim, sign included: the sign IS the direction
      // in this codebase (see getSignedWeight). Taking an absolute value here
      // would flip a held short into a long on a row the editor never touched.
      // `position` is only the literal the payload type requires; it is a text
      // declaration and must never be treated as the source of direction.
      weight: getSignedWeight(record),
      position: "LONG" as const,
    }));

  return preserved.length > 0 ? [...strategies, ...preserved] : strategies;
}

export interface StrategyUpdateEntry {
  id: number;
  investorStrategyId: number;
  method: "POST" | "PUT" | "DELETE";
  urlPath: string | null;
  queryParams: string | null;
  payload: Record<string, unknown>;
  datetimeUtc: string;
  userId: string;
}

export interface PaginatedStrategyUpdates {
  currentPage: number;
  itemsOnPage: number;
  totalPages: number;
  totalItems: number;
  items: StrategyUpdateEntry[];
}

export interface SyncResult {
  created: number;
  updated: number;
  removed: number;
  unchanged: number;
  strategies: StrategyEntry[];
}

// Matches the row shape returned by the admin bulk endpoint (OpenAPI `UserConfiguration`
// schema in plutus-backend/docs/api/investor-strategy-api.openapi.json). Kept separate from
// the legacy `UserConfiguration` in `UserConfigurationService.ts` because field names differ
// (`createdAtUtc` vs `createdAt`, `id` integer vs string, free-form `data` vs stringly-typed).
export interface BulkUserConfiguration {
  id: number;
  userId: string;
  ibkrAccountId: string | null;
  data: Record<string, unknown> | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface BulkUserStrategy {
  userId: string;
  userConfig: BulkUserConfiguration | null;
  strategies: StrategyEntry[];
  isStrategyStale: boolean;
  dataInconsistent: boolean;
}

export interface BulkUserStrategiesResult {
  totalUsers: number;
  users: BulkUserStrategy[];
}

interface GetStrategiesResponse {
  message: string;
  data: InvestorStrategyRecord[];
}

interface SyncStrategiesResponse {
  message: string;
  data: SyncResult;
}

interface BulkUserStrategiesResponse {
  message: string;
  data: BulkUserStrategiesResult;
}

const STRATEGY_FACTORS_PAGE_SIZE = 10000;

// Weight sign is the source of truth. Legacy text fields can be displayed, but must not
// drive strategy math.
export function getSignedWeight(entry: Pick<StrategyEntry, "weight">): number {
  const weight = parseFloat(entry.weight);
  if (!Number.isFinite(weight)) return 0;
  return weight;
}

export function getSignedDriftedWeight(
  entry: Pick<StrategyEntry, "driftedWeight">
): number | null {
  if (entry.driftedWeight === null || entry.driftedWeight === undefined) return null;
  if (!Number.isFinite(entry.driftedWeight)) return null;
  return entry.driftedWeight;
}

// Service

class InvestorStrategyService {
  private readonly basePath = "/v2/investor/strategy";

  async getStrategyRecords(): Promise<InvestorStrategyRecord[]> {
    try {
      const response = await httpService.get<GetStrategiesResponse>(this.basePath);
      return response.data.data || [];
    } catch (error) {
      console.error("Get investor strategies error:", error);
      throw error;
    }
  }

  async getStrategies(): Promise<StrategyEntry[]> {
    const records = await this.getStrategyRecords();
    return records.filter(isSelfDirectedRecord);
  }

  // NOTE: this PUT replaces the full strategy set -- an entry omitted from the
  // payload is removed. A previous guard rejected payloads that dropped an
  // existing MANAGED_STRATEGY; it was removed deliberately, because Plutus
  // Managed is an ordinary product that must be removable (see
  // docs/specs/alpha-discover-allocation-integration-2026-08-18.md, section 2.3).
  //
  // Callers that can express every entity type (the alpha allocations editor)
  // use this directly and stay in full control, including deliberate removal.
  // Callers that CANNOT -- the legacy and admin strategy editors, which read
  // through getStrategies()/getStrategiesForUser() and never see managed rows --
  // must use syncSelfDirectedStrategies* below so they cannot delete a holding
  // they never rendered.
  async syncStrategies(
    strategies: StrategySyncInput[]
  ): Promise<SyncResult> {
    try {
      const response = await httpService.put<SyncStrategiesResponse>(
        this.basePath,
        { strategies }
      );
      return response.data.data;
    } catch (error) {
      console.error("Sync strategies error:", error);
      throw error;
    }
  }

  // Save a payload that only expresses self-directed products (PORTFOLIO /
  // BASKET), preserving anything currently held that the caller cannot render.
  // "Do not delete what you cannot see": this is a general rule, not a managed
  // carve-out, and it stays correct for any future entity type these editors do
  // not handle. If the read of the current set fails we save the payload as-is
  // rather than blocking the user's edit.
  async syncSelfDirectedStrategies(
    strategies: StrategySyncInput[]
  ): Promise<SyncResult> {
    const current = await this.readCurrentStrategiesForPreserve(
      () => this.getStrategyRecords()
    );
    return this.syncStrategies(withPreservedEntries(strategies, current));
  }

  async syncSelfDirectedStrategiesForUser(
    userId: string,
    strategies: StrategySyncInput[]
  ): Promise<SyncResult> {
    return this.syncRenderedStrategiesForUser(userId, strategies, isSelfDirectedRecord);
  }

  // General form of the rule above: save a payload that expresses everything
  // the calling editor renders, preserving only what it does not. An editor
  // that renders managed strategies passes isAdminEditableRecord, so removing a
  // managed row actually removes it -- while a future entity type nobody has
  // taught the editor about is still never deleted by a save from here.
  async syncRenderedStrategiesForUser(
    userId: string,
    strategies: StrategySyncInput[],
    canRender: (record: InvestorStrategyRecord) => boolean
  ): Promise<SyncResult> {
    const current = await this.readCurrentStrategiesForPreserve(
      () => this.getStrategyRecordsForUser(userId)
    );
    return this.syncStrategiesForUser(
      userId,
      withPreservedEntries(strategies, current, canRender)
    );
  }

  private async readCurrentStrategiesForPreserve(
    read: () => Promise<InvestorStrategyRecord[]>
  ): Promise<InvestorStrategyRecord[]> {
    try {
      return await read();
    } catch {
      // Preservation is best effort: never block a save because the read failed.
      return [];
    }
  }

  async getStrategyFactorsPage(
    params: { page?: number; pageSize?: number; from?: string; to?: string } = {}
  ): Promise<FactorsResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("page", String(params.page ?? 1));
      queryParams.set("pageSize", String(params.pageSize ?? STRATEGY_FACTORS_PAGE_SIZE));
      if (params.from) queryParams.set("from", params.from);
      if (params.to) queryParams.set("to", params.to);
      const url = `/v3/investor/strategy/factors?${queryParams.toString()}`;
      const response = await httpService.get<FactorsResponse>(url);
      return response.data;
    } catch (error) {
      console.error("Get strategy factors error:", error);
      throw error;
    }
  }

  async getStrategyUpdates(
    params: { page?: number; pageSize?: number } = {}
  ): Promise<PaginatedStrategyUpdates> {
    try {
      const page = params.page ?? 1;
      const pageSize = params.pageSize ?? 20;
      const queryParams = new URLSearchParams();
      queryParams.set("page", String(page));
      queryParams.set("pageSize", String(pageSize));
      const url = `${this.basePath}/updates?${queryParams.toString()}`;
      const response = await httpService.get<PaginatedStrategyUpdates>(url);
      return response.data;
    } catch (error) {
      console.error("Get strategy updates error:", error);
      throw error;
    }
  }

  async getAllStrategies(): Promise<BulkUserStrategiesResult> {
    try {
      const response = await httpService.get<BulkUserStrategiesResponse>(
        `${this.basePath}/all`
      );
      const data = response.data?.data;
      // Treat a missing envelope as an empty result (true "no users" case).
      if (data == null) {
        return { totalUsers: 0, users: [] };
      }
      // Validate the shape explicitly. If the backend route is not deployed yet, the
      // request falls through to `/strategy/:userId` with userId="all" and returns an
      // array instead of the `{ totalUsers, users }` envelope. Fail loud so the admin
      // page surfaces its error banner rather than a misleading empty table.
      if (
        typeof data !== "object" ||
        Array.isArray(data) ||
        !Array.isArray((data as BulkUserStrategiesResult).users) ||
        typeof (data as BulkUserStrategiesResult).totalUsers !== "number"
      ) {
        throw new Error(
          "Unexpected response from GET /v2/investor/strategy/all — the bulk endpoint may not be deployed on this environment."
        );
      }
      return data as BulkUserStrategiesResult;
    } catch (error) {
      console.error("Get all investor strategies error:", error);
      throw error;
    }
  }

  async getStrategiesForUser(userId: string): Promise<StrategyEntry[]> {
    const records = await this.getStrategyRecordsForUser(userId);
    return records.filter(isSelfDirectedRecord);
  }

  // Public because callers that must account for holdings they cannot render
  // (the admin strategy editor's allocation budget) need the unfiltered set,
  // not the self-directed view getStrategiesForUser returns.
  async getStrategyRecordsForUser(userId: string): Promise<InvestorStrategyRecord[]> {
    try {
      const response = await httpService.get<GetStrategiesResponse>(
        `${this.basePath}/${userId}`
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Get investor strategies for user error:", error);
      throw error;
    }
  }

  async getStrategyUpdatesForUser(
    userId: string,
    params: { page?: number; pageSize?: number } = {}
  ): Promise<PaginatedStrategyUpdates> {
    try {
      const page = params.page ?? 1;
      const pageSize = params.pageSize ?? 20;
      const queryParams = new URLSearchParams();
      queryParams.set("page", String(page));
      queryParams.set("pageSize", String(pageSize));
      const url = `${this.basePath}/${userId}/updates?${queryParams.toString()}`;
      const response = await httpService.get<PaginatedStrategyUpdates>(url);
      return response.data;
    } catch (error) {
      console.error("Get strategy updates for user error:", error);
      throw error;
    }
  }

  async syncStrategiesForUser(
    userId: string,
    strategies: StrategySyncInput[]
  ): Promise<SyncResult> {
    try {
      const response = await httpService.put<SyncStrategiesResponse>(
        `${this.basePath}/${userId}`,
        { strategies }
      );
      return response.data.data;
    } catch (error) {
      console.error("Sync strategies for user error:", error);
      throw error;
    }
  }

  async getStrategyFactorsPageForUser(
    userId: string,
    params: { page?: number; pageSize?: number; from?: string; to?: string } = {}
  ): Promise<FactorsResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("page", String(params.page ?? 1));
      queryParams.set("pageSize", String(params.pageSize ?? STRATEGY_FACTORS_PAGE_SIZE));
      if (params.from) queryParams.set("from", params.from);
      if (params.to) queryParams.set("to", params.to);
      const url = `/v3/investor/strategy/factors/${userId}?${queryParams.toString()}`;
      const response = await httpService.get<FactorsResponse>(url);
      return response.data;
    } catch (error) {
      console.error("Get strategy factors for user error:", error);
      throw error;
    }
  }
}

export const investorStrategyService = new InvestorStrategyService();
export default investorStrategyService;
